
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SunStatus } from '@/types/terrace';

interface UseSunStatusApiProps {
  terraceId: string;
  setIsUpdating: (updating: boolean) => void;
  setError: (error: string | null) => void;
  setUpdatedStatus: (status: SunStatus) => void;
  setLastChecked: (timestamp: string) => void;
  isUpdating: boolean;
}

export const useSunStatusApi = ({
  terraceId,
  setIsUpdating,
  setError,
  setUpdatedStatus,
  setLastChecked,
  isUpdating
}: UseSunStatusApiProps) => {
  const callSunStatusApi = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isUpdating) {
      if (import.meta.env.DEV) console.log('🌞 Update already in progress, skipping');
      return false;
    }

    try {
      // Create a simplified request body to avoid DataCloneError
      const requestBody = { terraceId: String(terraceId) };
      
      const { data, error } = await supabase.functions.invoke('calculate-sun-status', {
        body: requestBody
      });

      if (error) {
        // Handle DataCloneError and similar serialization errors silently
        if (error.message?.includes('DataCloneError') || error.message?.includes('postMessage')) {
          if (import.meta.env.DEV) console.log('🌞 DataCloneError detected, skipping update');
          return false;
        }
        
        // Handle other errors more gracefully
        if (error.message?.includes('not found')) {
          setError('Terrace not found');
        } else if (error.message?.includes('rate limit') || error.message?.includes('429')) {
          setError('Too many requests, please wait a moment');
        } else {
          setError(`Update failed: ${error.message || 'Unknown error'}`);
        }
        return false;
      }

      if (data?.status) {
        setUpdatedStatus(data.status);
        setLastChecked(data.calculatedAt);
        setError(null);
        return true;
      } else {
        setError('No status data received');
        return false;
      }
    } catch (error: any) {
      // Handle network errors specifically and prevent page refreshs
      if (error.name === 'DataCloneError' || error.message?.includes('DataCloneError')) {
        if (import.meta.env.DEV) console.log('🌞 Caught DataCloneError, skipping update');
        return false;
      }
      
      if (error.message?.includes('fetch') || error.message?.includes('network')) {
        setError('Network connection issue');
      } else {
        setError(`Network error: ${error.message || 'Connection failed'}`);
      }
      return false;
    }
  }, [terraceId, setIsUpdating, setError, setUpdatedStatus, setLastChecked, isUpdating]);

  return { callSunStatusApi };
};
